import { useMemo } from 'react';
import Image from 'next/image';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/common/Select';
import { WebBodyText14, WebCaption12 } from '@fintronners/react-ui/src/GlobalStyling/webTypography';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { TradeSide } from '@/components/common/SidePanels/TradeSidePanel/schema';
import { useTranslation } from 'react-i18next';
import { InfoWithIcon } from '@/components/common/SidePanels/TransferSidePanel/parts/InfoWithIcon';
import {
  NumberNoNaN,
  thousandFormatNumbroCurrency,
} from '@fintronners/react-utils/src/numberUtilsTSX';
import CurrencyInput from 'react-currency-input-field';
import { BaseFormProps } from '../FixedIncomeTradeForm';
import FixedIncomeTradeBreakdown from '../breakdown/FixedIncomeTradeBreakdown';
import { FIXED_INCOME_LIMIT_BUY_PRICE_MARGIN } from '@fintronners/internal-core/src/trade/constants';
import { AssetPriceType } from '@fintronners/react-api/src';
import {
  FixedIncomeTradeFormKeys,
  FixedIncomeTradeFormValues,
} from '@/components/common/SidePanels/TradeSidePanel/FixedIncome/schema';
import { InfoErrorIcon } from '@/assets/icons';

/**
 * Fixed Income Trade Buy Form Component
 *
 * This component renders the form for placing a buy order for fixed income securities.
 * It includes fields for selecting the trade side, entering the limit price, and specifying the quantity of bonds to buy.
 * It also displays a breakdown of the trade costs and provides a warning about the limit price.
 */
export const FixedIncomeTradeBuyForm = ({ asset, breakdown }: BaseFormProps) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<FixedIncomeTradeFormValues>();
  const { t } = useTranslation();

  const amount = useWatch({
    control,
    name: FixedIncomeTradeFormKeys.AMOUNT,
  });

  const assetMarket = asset?.fixedIncomeMarket;

  const askPercentageOfParPrice = useMemo(
    () =>
      assetMarket?.askPrices.find(
        (price) => price.type === AssetPriceType.AssetPriceTypePercentageOfPar,
      )?.price,
    [assetMarket?.askPrices],
  );

  const askPriceWithMargin = askPercentageOfParPrice
    ? Number(askPercentageOfParPrice) + FIXED_INCOME_LIMIT_BUY_PRICE_MARGIN
    : 0;

  return (
    <>
      <div>
        <Controller
          control={control}
          name={FixedIncomeTradeFormKeys.SIDE}
          render={({ field: { value, onChange } }) => (
            <Select value={value} defaultValue={value} onValueChange={onChange} disabled>
              <SelectTrigger className="my-7">
                <SelectValue placeholder={t('common.selectCategory')} />
              </SelectTrigger>

              <SelectContent>
                <SelectGroup>
                  <SelectItem value={TradeSide.BUY}>
                    {t('miscScreens.tradeOptions.tradeLimitBuyOrderType')}
                  </SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          )}
        />

        <div className="flex flex-col gap-1">
          <WebBodyText14.Regular className="text-grey72">
            {t('miscScreens.tradeOptions.tradeLimitBuyOrderLimit')}
          </WebBodyText14.Regular>

          <WebCaption12.Regular className="text-grey72">
            {t('miscScreens.tradeOptions.fixedIncome.limitDesc')}
          </WebCaption12.Regular>

          <Controller
            control={control}
            name={FixedIncomeTradeFormKeys.LIMIT_PRICE}
            render={({ field: { value, onChange } }) => (
              <CurrencyInput
                className="mt-2 w-full border rounded h-[35px] px-3 py-2 text-sm text-darkestBlue focus-visible:outline-primary"
                value={value}
                placeholder="0.00"
                decimalsLimit={2}
                intlConfig={{ locale: 'en-US' }}
                onValueChange={(_value) => onChange(NumberNoNaN(_value))}
              />
            )}
          />

          <div className="mt-[10px]">
            <InfoWithIcon
              message={t('miscScreens.tradeOptions.fixedIncome.limitPriceWarning', {
                limitPrice: thousandFormatNumbroCurrency(askPriceWithMargin),
              })}
            />
          </div>
        </div>

        <div className="my-7">
          <WebBodyText14.Regular className="text-grey72">
            {t('miscScreens.tradeOptions.fixedIncome.bondsQuantity')}
          </WebBodyText14.Regular>

          <Controller
            control={control}
            name={FixedIncomeTradeFormKeys.AMOUNT}
            render={({ field: { value, onChange } }) => (
              <CurrencyInput
                className="w-full text-[60px] text-primary outline-none disabled:text-grey25 bg-transparent"
                value={value || ''}
                intlConfig={{ locale: 'en-US' }}
                onValueChange={(_value) => onChange(NumberNoNaN(_value))}
                allowNegativeValue={false}
                allowDecimals={false}
                placeholder="0"
              />
            )}
          />

          {errors[FixedIncomeTradeFormKeys.AMOUNT]?.message && amount > 0 && (
            <div className="flex items-start gap-2">
              <Image alt="Info icon" width={14} height={14} src={InfoErrorIcon.src} />

              <WebCaption12.Regular className="text-red">
                {errors[FixedIncomeTradeFormKeys.AMOUNT]?.message}
              </WebCaption12.Regular>
            </div>
          )}
        </div>

        <FixedIncomeTradeBreakdown breakdown={breakdown} />
      </div>
    </>
  );
};
